import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Scene } from '@babylonjs/core/scene';

import '@babylonjs/core/Rendering/outlineRenderer';

import { highlightParts, silhouetteOutline } from './outline';
import type { ItemRarity } from '../net/protocol';
import type { GameStore } from '../state/store';
import { ruleHides, type FilterTier, type FilterVerdict } from './filter';
import { MIN_OUTLINE_WIDTH } from './pixelate';

/**
 * Rarity colours, matching the loot table in doc/grind/design/ECONOMY-LOOT.md
 * and the CSS tokens the vault uses. Four tiers — there is no Legendary.
 */
const RARITY_COLOUR: Record<ItemRarity, [number, number, number]> = {
  NORMAL: [0.78, 0.78, 0.78],
  MAGIC: [0.34, 0.5, 0.85],
  RARE: [0.83, 0.78, 0.29],
  UNIQUE: [0.84, 0.55, 0.23],
};

/** Height of the beam marking a drop. Tall enough to spot across a room. */
const BEAM_HEIGHT = 2.2;

/**
 * The diameter the cylinder is *built* at, and therefore the denominator for a tier's
 * scale factor. Equal to `TIER_BEAM_DIAMETER[4]`, so an unprioritised drop is scale 1.
 */
const BASE_BEAM_DIAMETER = 0.16;

/** How fast the loot marker spins, in radians per second. */
const SPIN_RATE = 1.1;

/**
 * Size of a drop's click target, in world units.
 *
 * Much larger than the shard itself, which is 0.34 and spinning. Aiming at a
 * rotating 0.34-unit cube at gameplay camera distance is a game of its own.
 */
const HITBOX_SIZE = 1.8;

/**
 * The hover outline for a drop — **one output pixel, the same as an entity's**.
 *
 * It used to be 0.16 against the entity's 0.2, on the reasoning that a shard is a fraction of a
 * character's size so an equally thick rim would swallow it. That reasoning was right and is now
 * unexpressible: both were lowered onto `MIN_OUTLINE_WIDTH` when a 0.2 rim turned out to be
 * *filling in* every part thinner than 0.4 (owner's report — Babylon scales the mesh to draw an
 * outline, so the width is measured against the part, not the screen).
 *
 * There is nowhere tighter to go. One output pixel is the thinnest line the pixelate pass can
 * carry — below it the rim is averaged away and the hover is invisible, which is what both of
 * these shipped as at 0.07 and 0.08. So a drop and a monster now share the floor, and "a drop's
 * rim is tighter" is no longer a claim this renderer can make.
 */
export const LOOT_HIGHLIGHT_WIDTH = MIN_OUTLINE_WIDTH;
const LOOT_HIGHLIGHT_COLOUR = new Color3(0.95, 0.8, 0.45);

// ── Beam styling by filter priority ─────────────────────────────────────────
//
// The beam half of the verdict → presentation mapping. It lives here rather than in
// `game/filter/style.ts` beside the label half, because the floor on a beam's width
// comes from `game/pixelate.ts` and that module imports Babylon — putting it in the
// filter directory would put Babylon in the import graph of the one place that claims
// to have none.

/**
 * How tall a beam is, as a multiple of `BEAM_HEIGHT`.
 *
 * **Tier 4 is 1.0 — today's beam, unchanged — and the ladder only goes up.** The spec's
 * tier table says Tier 4 gets "No light beam", and that is a sensible thing for a
 * *player* to ask for about trash. But Tier 4 is also what an item gets when nothing
 * assigned one, so honouring it literally would delete the beam from every Normal drop
 * in the game the moment the filter was wired — taking away something visible today
 * from a player who never opened the editor. A `Hide` rule is how a beam is removed; a
 * tier is a priority, and making `SetTier 4` a second and surprising way to hide
 * something would be a worse language. Recorded as a deviation in
 * `doc/grind/design/LOOT-FILTER.md`.
 */
export const TIER_BEAM_HEIGHT: Readonly<Record<FilterTier, number>> = Object.freeze({
  1: 2.2,
  2: 1.6,
  3: 1.15,
  4: 1,
});

/**
 * How wide a beam is, in world units.
 *
 * **Every entry must clear `MIN_OUTLINE_WIDTH`** (`PIXEL_SIZE / PIXELS_PER_UNIT` ≈ 0.12).
 * A beam is a cylinder rather than an outline, but it is subject to the same arithmetic:
 * one world unit is about 25 device pixels at the gameplay camera and the pixelate
 * post-process renders at 1/`PIXEL_SIZE`, so anything below that floor is averaged away
 * and the drop simply has no beam, with nothing in the console to say why. Tier 4 is the
 * existing 0.16 and `loot.test.ts` holds the whole table above the floor.
 */
export const TIER_BEAM_DIAMETER: Readonly<Record<FilterTier, number>> = Object.freeze({
  1: 0.3,
  2: 0.24,
  3: 0.18,
  4: 0.16,
});

/** How to draw one drop's beam and its shard. */
export interface BeamStyle {
  tier: FilterTier;
  /**
   * The **pillar of light**, and since 2026-08-13 it follows the filter's verdict exactly.
   *
   * > Owner's call: *by default, no beam for normal and magic, just a moving square so we can
   * > see there is something, and the label on Alt. Beam should only be there without Alt when
   * > there is a rare or unique, unless you change the loot filter.*
   *
   * That is precisely `verdict.visible`: the built-in fallback shows Rare and better
   * (`DEFAULT_MIN_RARITY`), and a rule overrides it in either direction. So a floor of Normal
   * drops is now dark, and the filter is the single thing that decides otherwise.
   *
   * **This reverses an earlier decision, deliberately.** The old rule was "only a *rule* hides a
   * beam" — the built-in fallback never did — on the reasoning that honouring the spec's tier
   * table literally would delete the beam from every Normal drop for a player who never opened
   * the editor. That reasoning is now spent: `Alt` brings every hidden beam back, so nothing is
   * unreachable, and the thing being protected turned out to be the clutter itself.
   */
  visible: boolean;
  /**
   * The little spinning **shard** on the floor, which is a weaker claim than the beam.
   *
   * *"Just a moving square so we can see there is something."* A Normal drop keeps its object
   * even with no pillar over it — that is how a player knows the floor is not empty, and it is
   * what keeps the drop clickable, since the collider follows what is drawn.
   *
   * Only an explicit `Hide` **rule** takes it away. The Rare+ fallback governs the beam and the
   * label; it must not make an item invisible on a floor the player is walking across.
   */
  shardVisible: boolean;
  /** `PlayLightBeam`, as `#rrggbb`, or null for the rarity colour. */
  colour: string | null;
  /** Multiplier on `BEAM_HEIGHT`. */
  heightScale: number;
  /** Diameter in world units, never below `MIN_OUTLINE_WIDTH`. */
  diameter: number;
}

/**
 * The beam for a drop the filter has judged.
 *
 * Pure, so the whole mapping is testable without a scene — which is the point of it
 * being a function rather than four branches inside `update`.
 */
export function beamStyle(verdict: FilterVerdict | undefined): BeamStyle {
  // No verdict at all means no filter has seen this drop yet — a snapshot that arrived
  // before the memo recomputed, or a caller that does not have one. Tier 4 is the
  // beam it has always had, which is the right thing to draw while nothing knows better.
  const tier: FilterTier = verdict?.tier ?? 4;
  return {
    tier,
    // The filter's verdict, whole: a rule in either direction, or the Rare+ fallback.
    visible: verdict?.visible ?? true,
    // A weaker claim — only a rule takes the object off the floor. See `shardVisible`.
    shardVisible: verdict === undefined ? true : !ruleHides(verdict),
    colour: verdict?.style.beamColour ?? null,
    heightScale: TIER_BEAM_HEIGHT[tier],
    diameter: Math.max(MIN_OUTLINE_WIDTH, TIER_BEAM_DIAMETER[tier]),
  };
}

/**
 * Renders items lying on the floor.
 *
 * A drop is a spinning shard under a vertical beam of its rarity colour. Both are
 * fully emissive: the zones are near-black and a drop the player cannot see from
 * across a room might as well not have happened.
 *
 * Meshes are pooled by rarity — one material each, reused — so a floor covered in
 * loot is still only a handful of materials.
 */
export class LootMeshes {
  private readonly tracked = new Map<string, LootEntry>();
  /**
   * One material per colour, keyed by rarity name or by a filter's `#rrggbb`.
   *
   * Keyed by string rather than by rarity so a `PlayLightBeam` colour joins the same
   * pool: a filter that paints every unique crimson gets one extra material for the
   * whole floor, not one per drop.
   */
  private readonly materials = new Map<string, StandardMaterial>();
  private spin = 0;

  constructor(
    private readonly scene: Scene,
    private readonly store: GameStore,
  ) {}

  /**
   * Outlines a drop, or clears it.
   *
   * The same reasoning as entities: rarity materials are shared between every
   * drop of that colour, so a tint would light up the whole floor.
   */
  setHighlight(id: string, on: boolean): void {
    const entry = this.tracked.get(id);
    if (!entry) return;

    // The item's silhouette only: traced round the beam too, the outline became a lit column
    // standing on the floor. The per-mesh fallback keeps outlining both, as it always has.
    const parts = silhouetteOutline() ? [entry.shard] : [entry.shard, entry.beam];
    highlightParts(`loot:${id}`, parts, on, LOOT_HIGHLIGHT_WIDTH, LOOT_HIGHLIGHT_COLOUR);
  }

  /** Syncs the visible markers to the latest snapshot and spins them. */
  update(dt: number): void {
    this.spin = (this.spin + dt * SPIN_RATE) % (Math.PI * 2);

    // Read once per frame rather than per drop: it is one signal and a floor can hold dozens.
    const revealed = this.store.lootRevealed();
    const live = new Set<string>();

    for (const g of this.store.ground()) {
      live.add(g.id);
      const entry = this.tracked.get(g.id) ?? this.create(g.id, g.rarity, g.x, g.z);
      entry.shard.rotation.y = this.spin;
      // A slow bob, so a drop reads as an object rather than a decal.
      entry.shard.position.y = 0.55 + Math.sin(this.spin * 2) * 0.07;

      // The loot filter's presentation, re-read each frame but only *written* when it
      // actually changed. A player saving a filter mid-fight has to see it take effect
      // — "a setting must take effect immediately or not exist" — and comparing against
      // the applied style is what keeps that from being per-frame mesh work.
      this.applyFilter(entry, g.rarity, this.store.filterVerdict(g.id), revealed);
    }

    // Anything no longer in the snapshot was collected or the zone changed.
    for (const id of [...this.tracked.keys()]) {
      if (!live.has(id)) this.remove(id);
    }
  }

  /** Returns the loot id a picked mesh belongs to, or null. */
  idForMesh(mesh: unknown): string | null {
    const node = mesh as { metadata?: { lootId?: unknown } } | null | undefined;
    const id = node?.metadata?.lootId;
    return typeof id === 'string' ? id : null;
  }

  /**
   * Of every drop whose hitbox is under the pointer, the one **drawn nearest the pointer on
   * screen** — or `picked` when it is the only one.
   *
   * Babylon's pick returns the box nearest the *camera* along the ray, and the boxes are tall (the
   * beam's height) and generous, so two drops a metre apart overlap on screen: a click on the far
   * one landed on the near one's box and picked up the wrong item. Reported: *it's really hard to
   * pick up items, or the items we wanted.* Hover and click both ask this, so the item outlined is
   * the item taken.
   */
  resolveUnderPointer(picked: string, pointerX: number, pointerY: number): string {
    const scene = this.scene;
    const camera = scene.activeCamera;
    if (!camera || this.tracked.size < 2) return picked;
    const hits = scene.multiPick(pointerX, pointerY, (mesh) => mesh.isPickable && typeof mesh.metadata?.lootId === 'string') ?? [];
    if (hits.length < 2) return picked;
    const engine = scene.getEngine();
    const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
    const transform = scene.getTransformMatrix();
    let best = picked;
    let bestD = Infinity;
    for (const hit of hits) {
      const id = hit.pickedMesh?.metadata?.lootId as string | undefined;
      const entry = id ? this.tracked.get(id) : undefined;
      if (!id || !entry) continue;
      const p = Vector3.Project(entry.shard.getAbsolutePosition(), Matrix.IdentityReadOnly, transform, viewport);
      const d = (p.x - pointerX) ** 2 + (p.y - pointerY) ** 2;
      if (d < bestD) {
        bestD = d;
        best = id;
      }
    }
    return best;
  }

  dispose(): void {
    for (const id of [...this.tracked.keys()]) this.remove(id);
    for (const mat of this.materials.values()) mat.dispose();
    this.materials.clear();
  }

  private create(id: string, rarity: ItemRarity, x: number, z: number) {
    const material = this.material(rarity);

    const shard = MeshBuilder.CreateBox(
      `loot-${id}`,
      { width: 0.34, height: 0.34, depth: 0.34 },
      this.scene,
    );
    shard.position.set(x, 0.55, z);
    shard.rotation.x = Math.PI / 5;
    shard.material = material;
    // Visuals never take the click: the shard spins, so it is a moving target.
    shard.isPickable = false;

    const beam = MeshBuilder.CreateCylinder(
      `loot-beam-${id}`,
      { height: BEAM_HEIGHT, diameter: BASE_BEAM_DIAMETER, tessellation: 6 },
      this.scene,
    );
    beam.position.set(x, BEAM_HEIGHT / 2, z);
    beam.material = material;
    // The beam is decoration; clicking it would be a click that misses.
    beam.isPickable = false;

    // One generous invisible box covers shard and beam together.
    const hitbox = MeshBuilder.CreateBox(
      `loot-hit-${id}`,
      { width: HITBOX_SIZE, height: BEAM_HEIGHT, depth: HITBOX_SIZE },
      this.scene,
    );
    hitbox.position.set(x, BEAM_HEIGHT / 2, z);
    hitbox.isVisible = false;
    hitbox.isPickable = true;
    hitbox.metadata = { lootId: id };

    const entry: LootEntry = { shard, beam, hitbox, applied: null };
    this.tracked.set(id, entry);
    return entry;
  }

  /**
   * Applies the filter's beam styling, if it has changed since the last frame.
   *
   * The comparison is the whole reason this is cheap: a verdict is stable for the life of
   * a drop unless the player saves a new filter, so in the ordinary case this is a string
   * compare per drop per frame and no mesh writes at all.
   *
   * **The hitbox follows visibility.** It used to be left alone on the reasoning that hiding is
   * presentation and a hidden drop is still lying on that floor — true, and it made an invisible
   * drop eat clicks aimed at the monster standing on it. Since the reveal key that is the
   * player's own choice again: a `Hide` rule removes the click target with the mesh, and holding
   * Alt restores both. See the note on `isPickable` below.
   *
   * @param revealed Whether the player is holding the reveal key, which shows drops a `Hide` rule
   *   removed. Only ever *adds*: a key that hid something would be indistinguishable from the
   *   drop despawning, and the filter is the player's own configuration.
   */
  private applyFilter(
    entry: LootEntry,
    rarity: ItemRarity,
    verdict: FilterVerdict | undefined,
    revealed: boolean,
  ): void {
    const style = beamStyle(verdict);
    // Alt brings a hidden drop back into the world — both the object and its pillar.
    const shard = style.shardVisible || revealed;
    const beam = style.visible || revealed;
    // Both join the memo key, or the first frame of a reveal compares equal to the last frame
    // before it and the floor lights up only once something else dirties the entry.
    const key = `${style.tier}|${shard}|${beam}|${style.colour ?? rarity}`;
    if (entry.applied === key) return;
    entry.applied = key;

    entry.shard.isVisible = shard;
    entry.beam.isVisible = beam;

    /**
     * ── Click-through for hidden drops ─────────────────────────────────────────────────
     *
     * *"Clicking the floor where a hidden item sits will move your character or attack an enemy
     * instead of accidentally grabbing invisible loot."*
     *
     * **Tied to what is drawn, not to what is labelled**, which is a deliberate departure from
     * the idea's own template. It ties the collider to the *text card*, and here that would
     * leave a Normal drop with a visible beam and shard that swallows no clicks — a thing you
     * can see, cannot pick up, and are given no reason for. The rule that survives contact with
     * this renderer is simpler and stronger: **you can click exactly what you can see.**
     *
     * It follows the **shard**, not the beam: a Normal drop has no pillar since 2026-08-13 and is
     * still lying right there, still collectable. Only a `Hide` rule takes the object away, and
     * holding the reveal key brings it back.
     */
    entry.hitbox.isPickable = shard;

    const material = style.colour === null ? this.material(rarity) : this.hexMaterial(style.colour);
    entry.shard.material = material;
    entry.beam.material = material;

    // Scaled rather than rebuilt: the cylinder is 0.16 across and `BEAM_HEIGHT` tall, so
    // a tier is two multipliers and a reposition. Rebuilding geometry every time a
    // player edited their filter would churn a mesh per drop for a scale change.
    entry.beam.scaling.y = style.heightScale;
    entry.beam.scaling.x = style.diameter / BASE_BEAM_DIAMETER;
    entry.beam.scaling.z = entry.beam.scaling.x;
    entry.beam.position.y = (BEAM_HEIGHT * style.heightScale) / 2;
  }

  private remove(id: string): void {
    const entry = this.tracked.get(id);
    if (!entry) return;
    entry.shard.dispose();
    entry.beam.dispose();
    entry.hitbox.dispose();
    this.tracked.delete(id);
  }

  private material(rarity: ItemRarity): StandardMaterial {
    const rgb = RARITY_COLOUR[rarity] ?? RARITY_COLOUR.NORMAL;
    return this.emissive(rarity, rgb);
  }

  /** The same, for a `PlayLightBeam` colour the filter chose. */
  private hexMaterial(hex: string): StandardMaterial {
    return this.emissive(hex, hexToRgb(hex));
  }

  private emissive(key: string, rgb: readonly [number, number, number]): StandardMaterial {
    const existing = this.materials.get(key);
    if (existing) return existing;

    const mat = new StandardMaterial(`loot-${key}`, this.scene);
    mat.diffuseColor = new Color3(rgb[0] * 0.4, rgb[1] * 0.4, rgb[2] * 0.4);
    // Fully emissive: a drop must be visible in an unlit corridor.
    mat.emissiveColor = new Color3(rgb[0], rgb[1], rgb[2]);
    mat.specularColor = new Color3(0, 0, 0);
    mat.alpha = 0.85;

    this.materials.set(key, mat);
    return mat;
  }
}

/** One drop's meshes, plus the beam style already written to them. */
interface LootEntry {
  shard: Mesh;
  beam: Mesh;
  hitbox: Mesh;
  /**
   * A key for the applied style, or null before the first application.
   *
   * A string rather than the `BeamStyle` object so the comparison is one `===` — a deep
   * compare per drop per frame is the kind of thing that quietly costs a frame budget.
   */
  applied: string | null;
}

/**
 * Parses `#rrggbb` into Babylon's 0..1 channels.
 *
 * Its own function rather than `Color3.FromHexString` so this file stays testable
 * without a scene, and so a malformed string is a decision rather than an exception:
 * anything unparseable comes back mid-grey, which is visible. A beam that threw would
 * take the render loop down over a typo in a player's filter, and one that came back
 * black would be invisible on a near-black floor — indistinguishable from the beam not
 * existing, which is the trap a skill's `color_palette` fallback exists to avoid.
 */
export function hexToRgb(hex: string): [number, number, number] {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return [0.55, 0.55, 0.55];
  const n = Number.parseInt(match[1]!, 16);
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
}
